import { useMemo } from 'react';

/**
 * Returns a stable JSON string representation of `value`.
 *
 * Useful as a dependency-array item when a hook needs to react to deep
 * changes in an object or array without causing spurious re-runs on
 * every render (since a new object reference would normally trigger the
 * effect even when contents are identical).
 *
 * Falls back to `String(value)` when `JSON.stringify` throws (e.g.
 * circular references).
 *
 * @example
 * ```ts
 * const filtersKey = useStableKey(tableState.state.filters);
 *
 * useEffect(() => {
 *   // only fires when the filters actually change
 * }, [filtersKey]);
 * ```
 */
export function useStableKey(value: unknown): string {
  return useMemo(() => {
    try {
      return JSON.stringify(value ?? '');
    } catch {
      return String(value);
    }
  }, [value]);
}
